import { cn } from '../lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export const MetricCard = ({ metric, className }) => {
  const { name, value, target, score, status } = metric;

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'border-l-emerald-500 bg-emerald-50/50';
      case 'good': return 'border-l-lime-500 bg-lime-50/50';
      case 'average': return 'border-l-amber-500 bg-amber-50/50';
      case 'poor': return 'border-l-red-500 bg-red-50/50';
      default: return 'border-l-slate-300 bg-slate-50/50';
    }
  };

  const getScoreBadgeColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500 text-white';
      case 'good': return 'bg-lime-500 text-white';
      case 'average': return 'bg-amber-500 text-white';
      case 'poor': return 'bg-red-500 text-white';
      default: return 'bg-slate-400 text-white';
    }
  };

  const getTrendIcon = () => {
    if (score >= 90) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (score <= 60) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const formatValue = (val) => {
    if (typeof val !== 'number') return val;
    if (name.includes('%') || name.includes('Rate') || name.includes('Attainment') || name.includes('Conversion') || name.includes('Flow') || name.includes('Sales %')) {
      return `${val.toFixed(1)}%`;
    }
    if (name.includes('$') || name.includes('Revenue') || name.includes('Sales') || name.includes('ATV') || name.includes('Per Cap')) {
      return `$${val.toFixed(2)}`;
    }
    return val.toFixed(2);
  };

  return (
    <div 
      className={cn(
        'metric-card border-l-4 transition-all duration-200 hover:shadow-md',
        getStatusColor(status),
        className
      )}
      data-testid={`metric-card-${name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex justify-between items-start mb-3">
        <h4 className="text-sm font-medium text-slate-700 leading-tight pr-2">{name}</h4>
        {getTrendIcon()}
      </div>
      
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <span className="text-2xl font-bold font-mono text-slate-900">
            {formatValue(value)}
          </span>
          <span className={cn(
            'px-2 py-0.5 rounded text-xs font-bold',
            getScoreBadgeColor(status)
          )}>
            {Math.round(score)}
          </span>
        </div>
        
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Target: {formatValue(target)}</span>
          <span className={cn(
            'font-medium',
            value >= target ? 'text-emerald-600' : 'text-red-600'
          )}>
            {value >= target ? '+' : ''}{((value - target) / target * 100).toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export const MetricCardCompact = ({ name, value, target, score, status }) => {
  const getStatusDot = (status) => {
    switch (status) {
      case 'excellent': return 'bg-emerald-500';
      case 'good': return 'bg-lime-500';
      case 'average': return 'bg-amber-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        <div className={cn('w-2 h-2 rounded-full', getStatusDot(status))} />
        <span className="text-sm text-slate-700">{name}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm font-semibold">{typeof value === 'number' ? value.toFixed(1) : value}</span>
        <span className={cn(
          'px-1.5 py-0.5 rounded text-xs font-bold text-white',
          getStatusDot(status)
        )}>
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};
